/**
 * Billing Helpers
 *
 * This file contains utility functions related to the Billing module.
 * These helpers are used to process, transform, and calculate billing data
 * across different billing periods and metrics.
 *
 * Functions included:
 * - calculateBillingTotal: Aggregates all billing metrics per month
 *
 * @module helpers/billing
 */

import { BillingBreakdown } from '../types/reporting';

/**
 * Calculates the total billing value for each month by summing all metric values.
 *
 * @example
 * ```typescript
 * const totals = calculateBillingTotal(billingData);
 * // Returns: [{ month: "January 2026", value: 47758 }]
 * ```
 */
export const calculateBillingTotal = (data: BillingBreakdown[]) => {
  return data.map(monthData => {
    const { month, ...metrics } = monthData;

    const total = Object.values(metrics).reduce((sum, value) => sum + value, 0);

    return {
      month,
      value: total,
    };
  });
};

/**
 * Calculates watch billing totals with breakdown by platform (iOS and Android).
 * Covers every watch metric — activations (`watch_activation_*`) and transfers
 * (`watch_transfer_*`) — since both are billed under the watch use case.
 *
 * @param data - Array of billing breakdown data by month
 * @returns Array of objects containing month, total watch events, and platform-specific totals
 *
 * @example
 * ```typescript
 * const data = [{
 *   month: "August 2026",
 *   watch_activation_android_es: 25,
 *   watch_activation_android_pos: 0,
 *   watch_activation_ios_es: 83,
 *   watch_activation_ios_pos: 0,
 *   watch_transfer_android: 0,
 *   watch_transfer_ios: 3,
 *   device_transfer_android: 11  // This will be ignored
 * }];
 *
 * const result = calculateWatchActivationTotal(data);
 * // [{
 * //   month: "August 2026",
 * //   value: 111,
 * //   ios: 86,
 * //   android: 25
 * // }]
 * ```
 */
export const calculateWatchActivationTotal = (data: BillingBreakdown[]) => {
  return data.map(monthData => {
    const { month, ...metrics } = monthData;

    const watchEntries = Object.entries(metrics).filter(([key]) => {
      return key.startsWith('watch_');
    });

    const watchTotal = watchEntries.reduce((sum, [, value]) => sum + value, 0);

    // `_ios`/`_android` without a trailing underscore: `watch_transfer_ios` has
    // no channel suffix, unlike `watch_activation_ios_es`.
    const iosTotal = watchEntries
      .filter(([key]) => key.includes('_ios'))
      .reduce((sum, [, value]) => sum + value, 0);

    const androidTotal = watchEntries
      .filter(([key]) => key.includes('_android'))
      .reduce((sum, [, value]) => sum + value, 0);

    return {
      month,
      value: watchTotal,
      ios: iosTotal,
      android: androidTotal,
    };
  });
};
